// SearchSection.jsx - Updated to store searches in database
import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../utils/auth";
import { useSearch } from '../utils/searchStorage';
import { 
  Search, 
  Zap, 
  ArrowRight, 
  Brain, 
  Target, 
  CheckCircle, 
  XCircle, 
  Wifi, 
  WifiOff,
  Eye,
  Sparkles,
  Globe,
  TrendingUp,
  Clock,
  Share2
} from "lucide-react";

export default function SearchSection({ limitedAccess = false, onSearch }) {
  const authContext = useAuth();
  const { search } = useSearch();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [error, setError] = useState(null);
  const [analysisStage, setAnalysisStage] = useState("");
  const [apiStatus, setApiStatus] = useState('checking');

  // Safe auth context extraction
  const {
    user = null,
    canPerformAction = () => true,
    updateUsage = () => Promise.resolve(),
    checkUsageLimit = () => ({ canUse: true, remaining: 10, limit: 10 }),
    getCurrentTier = () => 'free'
  } = authContext || {};

  // Check API status
  useEffect(() => {
    checkApiStatus();
  }, []);

  const checkApiStatus = async () => {
    try {
      setApiStatus('checking');
      const response = await fetch('/api/health');
      if (response.ok) {
        setApiStatus('online');
        console.log('✅ API is online');
      } else {
        setApiStatus('offline');
        console.log('⚠️ API is offline, using demo mode');
      }
    } catch (error) {
      setApiStatus('offline');
      console.log('⚠️ API is offline, using demo mode:', error.message);
    }
  };

  // Handle prefill from dashboard or other sources  
  useEffect(() => {
    if (location.state?.prefill) {
      const prefillUrl = location.state.prefill.trim();
      console.log('🔄 Prefill detected from dashboard:', prefillUrl);
      setSearchTerm(prefillUrl);
      
      setTimeout(() => {
        if (prefillUrl) {
          performAnalysis(prefillUrl);
        }
      }, 500);
      
      window.history.replaceState(null, '', location.pathname + location.search);
    }
  }, [location.state, location.pathname, location.search]);

  // Handle URL demo parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const demoQuery = urlParams.get('demo');
    
    if (demoQuery && !location.state?.prefill) {
      setSearchTerm(demoQuery);
      setTimeout(() => {
        if (demoQuery.trim()) {
          performAnalysis(demoQuery.trim());
        }
      }, 500);
    }
  }, [location.search, location.state]);

  // Call backend API to analyze and store website
  const analyzeWithAPI = async (url) => {
    try {
      const authHeaders = {};
      if (user) {
        const token = localStorage.getItem('coderated_auth_token');
        if (token) {
          authHeaders['Authorization'] = `Bearer ${token}`;
        }
      }

      const response = await fetch('/api/analyze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({
          url: url,
          analysis_type: 'comprehensive',
          priority: 'normal',
          includeScreenshot: false
        })
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ API analysis successful:', result);
      
      return {
        url: result.url,
        score: result.score,
        overallScore: result.score,
        scores: result.scores,
        insights: result.insights,
        timestamp: result.timestamp,
        analysis_id: result.analysis_id,
        id: result.analysis_id,
        metadata: result.metadata,
        demo_mode: result.demo_mode,
        analysis_time: result.analysis_time
      };

    } catch (error) {
      console.error('API analysis failed:', error);
      throw error;
    }
  };

  // Generate demo results (fallback)
  const generateDemoResults = (url) => {
    const normalizedUrl = url.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '');
    
    const demoProfiles = {
      'apple.com': {
        score: 94,
        scores: { design: 97, performance: 91, seo: 93, accessibility: 89, content: 95 },
        insights: {
          working: "Exceptional brand consistency and premium user experience design. Perfect visual hierarchy and seamless navigation flow.",
          improvements: "International page load speeds could be optimized with better CDN coverage and image compression.",
          strategic: "Market leadership position is unmatched in premium segment. Focus on emerging markets for expansion opportunities."
        },
        industry: 'Technology',
        tags: ['technology', 'mobile', 'premium', 'design']
      },
      'github.com': {
        score: 89,
        scores: { design: 92, performance: 86, seo: 88, accessibility: 87, content: 91 },
        insights: {
          working: "Developer-focused UI/UX excellence with clean code presentation and intuitive navigation for technical users.",
          improvements: "Mobile experience could be enhanced for better tablet usage. Onboarding process for new developers needs streamlining.",
          strategic: "Strong developer community engagement. Opportunity to expand into enterprise collaboration tools and project management."
        },
        industry: 'Development',
        tags: ['development', 'coding', 'open-source', 'collaboration']
      },
      'stripe.com': {
        score: 96,
        scores: { design: 98, performance: 94, seo: 95, accessibility: 92, content: 97 },
        insights: {
          working: "Outstanding technical documentation and developer experience. Clean, professional design with excellent conversion flow.",
          improvements: "Could expand educational content for non-technical decision makers and add more industry-specific examples.",
          strategic: "Market-leading position in developer tools. Opportunity to capture more enterprise accounts with enhanced onboarding."
        },
        industry: 'Fintech',
        tags: ['payments', 'fintech', 'api', 'developer-tools']
      }
    };

    const profile = demoProfiles[normalizedUrl];
    if (profile) {
      return {
        url: normalizedUrl,
        score: profile.score,
        overallScore: profile.score,
        scores: profile.scores,
        insights: profile.insights,
        industry: profile.industry,
        tags: profile.tags,
        timestamp: new Date().toISOString(),
        demo_mode: true,
        analysis_time: (2 + Math.random() * 2).toFixed(1) + " seconds",
        id: `demo_${Date.now()}`,
        metadata: {
          load_time: (1 + Math.random() * 3).toFixed(2) + 's',
          page_size: Math.floor(500 + Math.random() * 2000) + 'KB',
          mobile_friendly: true,
          technologies: Math.floor(8 + Math.random() * 15)
        }
      };
    }

    // Generic demo for unknown sites
    const baseScore = 70 + Math.floor(Math.random() * 25);
    return {
      url: normalizedUrl,
      score: baseScore,
      overallScore: baseScore,
      scores: {
        design: Math.max(60, baseScore + Math.floor(Math.random() * 10 - 5)),
        performance: Math.max(60, baseScore + Math.floor(Math.random() * 10 - 5)),
        seo: Math.max(60, baseScore + Math.floor(Math.random() * 10 - 5)),
        accessibility: Math.max(60, baseScore + Math.floor(Math.random() * 10 - 5)),
        content: Math.max(60, baseScore + Math.floor(Math.random() * 10 - 5))
      },
      insights: {
        working: "Good foundational structure with clear navigation hierarchy and responsive design elements.",
        improvements: "SEO optimization and page loading speeds present the biggest improvement opportunities for better performance.",
        strategic: "Website positioning shows potential for growth. Focus on content marketing and user engagement improvements."
      },
      industry: 'Business',
      tags: ['business', 'website'],
      timestamp: new Date().toISOString(),
      demo_mode: true,
      analysis_time: (2 + Math.random() * 2).toFixed(1) + " seconds",
      id: `demo_${Date.now()}`,
      metadata: {
        load_time: (1 + Math.random() * 3).toFixed(2) + 's',
        page_size: Math.floor(500 + Math.random() * 2000) + 'KB',
        mobile_friendly: Math.random() > 0.3,
        technologies: Math.floor(5 + Math.random() * 15)
      }
    };
  };

  // Main analysis function - UPDATED to try API first, then fallback to demo
  const performAnalysis = async (url) => {
    setIsLoading(true);
    setError(null);
    setSearchResults(null);
    setShowResults(false);
    setAnalysisStage("Initializing analysis...");

    try {
      let results = null;

      // Format URL
      let formattedUrl = url.trim();
      if (!formattedUrl.startsWith('http://') && !formattedUrl.startsWith('https://')) {
        formattedUrl = 'https://' + formattedUrl;
      }

      // Try API first if online
      if (apiStatus === 'online') {
        try {
          setAnalysisStage("Connecting to AI analysis system...");
          console.log('🚀 Starting API analysis for:', formattedUrl);
          
          results = await analyzeWithAPI(formattedUrl);
          setAnalysisStage("AI analysis completed successfully!");
          
        } catch (apiError) {
          console.log('❌ API analysis failed:', apiError.message);
          setApiStatus('offline');
          results = null;
        }
      }

      // Fallback to demo results if API failed or offline
      if (!results) {
        setAnalysisStage("Generating intelligent demo analysis...");
        console.log('🎭 Using demo results for:', url);
        
        // Simulate analysis time
        await new Promise(resolve => setTimeout(resolve, 1500 + Math.random() * 1000));
        
        results = generateDemoResults(url);
        setAnalysisStage("Demo analysis completed!");
      }

      // Store in local search context (for frontend display)
      if (search && search.addSearch) {
        console.log('💾 Storing in local search context...');
        const searchId = search.addSearch(results);
        console.log('✅ Local search stored with ID:', searchId);
      }

      // Show results
      setSearchResults(results);
      setShowResults(true);

      // Call parent onSearch if provided
      if (onSearch) {
        await onSearch(url);
      }

      console.log('🎉 Analysis completed successfully for:', url);

    } catch (error) {
      console.error('💥 Analysis failed:', error);
      setError(error.message || 'Analysis failed. Please try again.');
      setAnalysisStage("");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;

    // Check usage limits
    try {
      const canSearch = canPerformAction('searches');
      if (!canSearch) {
        setError("Search limit reached. Please upgrade to continue analyzing websites.");
        return;
      }

      // Update usage if authenticated
      if (user) {
        await updateUsage('searches');
        console.log('📊 Usage updated for user');
      }

      // Perform the analysis
      await performAnalysis(searchTerm.trim());

    } catch (error) {
      console.error('Search submission failed:', error);
      setError('Failed to start analysis. Please try again.');
    }
  };

  // Handle share
  const handleShare = () => {
    if (searchResults) {
      const shareUrl = `${window.location.origin}/analysis/${searchResults.id}`;
      const shareText = `Check out this AI analysis of ${searchResults.url} - Score: ${searchResults.score}/100! 🚀`;
      
      if (navigator.share) {
        navigator.share({
          title: `Website Analysis: ${searchResults.url}`,
          text: shareText,
          url: shareUrl
        }).catch(console.error);
      } else {
        navigator.clipboard.writeText(`${shareText}\n\n${shareUrl}`)
          .then(() => alert('Link copied to clipboard!'))
          .catch(() => {
            prompt('Copy this link:', shareUrl);
          });
      }
    }
  };

  // Get current tier info
  const currentTier = getCurrentTier();
  const searchInfo = checkUsageLimit('searches');

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background effects */}
      <div className="fixed inset-0 opacity-20">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-900/20 via-blue-900/20 to-cyan-900/20" />
      </div>

      {/* API Status Indicator */}
      <div className="fixed top-4 right-4 z-50">
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
          apiStatus === 'online' 
            ? 'bg-green-500/20 text-green-300 border border-green-500/30' 
            : apiStatus === 'offline'
            ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30'
            : 'bg-slate-500/20 text-slate-300 border border-slate-500/30'
        }`}>
          {apiStatus === 'online' ? (
            <>
              <Wifi className="w-3 h-3" />
              <span>Database Connected</span>
            </>
          ) : apiStatus === 'offline' ? (
            <>
              <WifiOff className="w-3 h-3" />
              <span>Demo Mode</span>
            </>
          ) : (
            <>
              <div className="w-3 h-3 border border-slate-400 border-t-transparent rounded-full animate-spin" />
              <span>Checking...</span>
            </>
          )}
        </div>
      </div>

      <div className="relative z-10 pt-20 pb-32 px-4">
        <div className="max-w-6xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-7xl font-black mb-8">
              <span className="bg-gradient-to-r from-white via-blue-100 to-purple-200 bg-clip-text text-transparent">
                AI Website Analysis
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-slate-300 mb-6 max-w-3xl mx-auto">
              {apiStatus === 'online' 
                ? 'Multi-agent AI system analyzing design, performance, SEO, and user experience with database persistence'
                : 'Experience intelligent demo analysis with realistic insights and recommendations'
              }
            </p>

            <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-8">
              {apiStatus === 'online' 
                ? 'Get comprehensive insights stored permanently in your dashboard and public feed'
                : 'Demo mode provides realistic sample data to showcase our analysis capabilities'
              }
            </p>

            {/* Usage display for authenticated users */}
            {user && searchInfo && (
              <div className="max-w-md mx-auto mb-8">
                <div className="bg-slate-800/50 border border-slate-600/50 rounded-lg p-4">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-slate-400">Searches Remaining</span>
                    <span className="text-white font-semibold">
                      {searchInfo.remaining} / {searchInfo.limit === -1 ? '∞' : searchInfo.limit}
                    </span>
                  </div>
                  {searchInfo.limit !== -1 && (
                    <div className="w-full bg-slate-700 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${Math.max(0, (searchInfo.remaining / searchInfo.limit) * 100)}%` }}
                      />
                    </div>
                  )}
                  <div className="text-xs text-slate-400 mt-2">
                    Plan: {currentTier.charAt(0).toUpperCase() + currentTier.slice(1)}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div className="max-w-4xl mx-auto mb-8">
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <div className="flex items-center gap-2 text-red-300">
                  <XCircle className="w-5 h-5" />
                  <span>{error}</span>
                </div>
              </div>
            </div>
          )}

          {/* Search Form */}
          <div className="max-w-4xl mx-auto mb-12">
            <form onSubmit={handleSubmit} className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-3xl blur-lg opacity-25 group-hover:opacity-40 transition duration-1000" />
              
              <div className="relative bg-slate-900/80 backdrop-blur-xl border border-slate-700/50 rounded-3xl p-2 flex items-center gap-3">
                <div className="flex-1 relative">
                  <div className="absolute left-6 top-1/2 transform -translate-y-1/2 text-slate-400">
                    <Search className="w-6 h-6" />
                  </div>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Enter website URL (e.g., apple.com, github.com, your-website.com)..."
                    className="w-full pl-16 pr-6 py-6 bg-transparent text-white placeholder-slate-400 focus:outline-none text-lg font-medium"
                    disabled={isLoading}
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={isLoading || !searchTerm.trim()}
                  className="px-8 py-6 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white font-bold rounded-2xl hover:shadow-2xl hover:shadow-blue-500/25 disabled:opacity-50 transition-all duration-300"
                >
                  <div className="flex items-center gap-3">
                    {isLoading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Analyzing</span>
                      </>
                    ) : (
                      <>
                        <Brain className="w-5 h-5" />
                        <span>Analyze</span>
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </div>
                </button>
              </div>
            </form>
            
            {/* Quick suggestions */}
            <div className="mt-4 text-center">
              <p className="text-slate-500 text-sm mb-2">Try these popular sites:</p>
              <div className="flex flex-wrap justify-center gap-2">
                {['apple.com', 'github.com', 'stripe.com', 'spotify.com'].map(site => (
                  <button
                    key={site}
                    onClick={() => setSearchTerm(site)}
                    className="px-3 py-1 bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 hover:text-white rounded-full text-xs transition-all"
                    disabled={isLoading}
                  >
                    {site}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Analysis Stage Display */}
          {isLoading && analysisStage && (
            <div className="max-w-2xl mx-auto mb-8">
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <div className="flex items-center gap-2 text-blue-300 text-sm">
                  <div className="w-4 h-4 border border-blue-400 border-t-transparent rounded-full animate-spin" />
                  <span>{analysisStage}</span>
                </div>
              </div>
            </div>
          )}

          {/* Results Display */}
          {showResults && searchResults && (
            <div className="max-w-4xl mx-auto mb-16">
              <div className="bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8">
                
                {/* Results Header */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-2">{searchResults.url}</h3>
                    <div className="flex items-center gap-2 text-slate-400 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span>Analysis completed in {searchResults.analysis_time}</span>
                      {searchResults.demo_mode ? (
                        <span className="ml-2 px-2 py-1 bg-yellow-500/20 text-yellow-300 text-xs rounded-full border border-yellow-500/30">
                          🎭 Demo
                        </span>
                      ) : (
                        <span className="ml-2 px-2 py-1 bg-green-500/20 text-green-300 text-xs rounded-full border border-green-500/30">
                          🤖 AI Analysis
                        </span>
                      )}
                      <span className="ml-2 px-2 py-1 bg-blue-500/20 text-blue-300 text-xs rounded-full border border-blue-500/30">
                        💾 Saved to Database
                      </span>
                    </div>
                  </div>
                  
                  {/* Overall Score */}
                  <div className="text-center">
                    <div className="text-5xl font-black mb-2">
                      <span className={`${
                        searchResults.score >= 90 ? 'text-emerald-400' :
                        searchResults.score >= 80 ? 'text-green-400' :
                        searchResults.score >= 70 ? 'text-yellow-400' :
                        searchResults.score >= 60 ? 'text-orange-400' :
                        'text-red-400'
                      }`}>
                        {searchResults.score}
                      </span>
                    </div>
                    <div className="text-slate-400 text-sm">Overall Score</div>
                  </div>
                </div>
                
                {/* Score breakdown */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                  {Object.entries(searchResults.scores || {}).map(([key, score]) => (
                    <div key={key} className="text-center bg-white/5 rounded-lg p-3">
                      <div className="text-white/60 text-xs mb-1 capitalize">{key}</div>
                      <div className={`text-lg font-semibold ${
                        score >= 90 ? 'text-emerald-400' :
                        score >= 80 ? 'text-green-400' :
                        score >= 70 ? 'text-yellow-400' :
                        score >= 60 ? 'text-orange-400' :
                        'text-red-400'
                      }`}>
                        {Math.round(score)}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Insights */}
                <div className="space-y-4">
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <h4 className="text-green-300 font-semibold mb-2 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      What's Working Well
                    </h4>
                    <p className="text-green-200 text-sm leading-relaxed">
                      {searchResults.insights?.working}
                    </p>
                  </div>
                  
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                    <h4 className="text-yellow-300 font-semibold mb-2 flex items-center gap-2">
                      <Target className="w-4 h-4" />
                      Improvement Opportunities
                    </h4>
                    <p className="text-yellow-200 text-sm leading-relaxed">
                      {searchResults.insights?.improvements}
                    </p>
                  </div>
                  
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <h4 className="text-blue-300 font-semibold mb-2 flex items-center gap-2">
                      <Brain className="w-4 h-4" />
                      Strategic Insights
                    </h4>
                    <p className="text-blue-200 text-sm leading-relaxed">
                      {searchResults.insights?.strategic}
                    </p>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="mt-8 pt-6 border-t border-slate-700/50 flex items-center justify-between">
                  <div className="text-sm text-slate-400">
                    <Clock className="w-4 h-4 inline mr-1" />
                    Analysis saved permanently • View in public feed
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <button 
                      onClick={handleShare}
                      className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-all"
                    >
                      <Share2 className="w-5 h-5" />
                      Share
                    </button>
                    
                    <Link 
                      to="/feed" 
                      className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-all"
                    >
                      <Eye className="w-5 h-5" />
                      View in Feed
                    </Link>
                    
                    {user && (
                      <button 
                        onClick={() => {
                          setSearchTerm('');
                          setSearchResults(null);
                          setShowResults(false);
                        }}
                        className="flex items-center gap-2 border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white px-6 py-3 rounded-lg font-semibold transition-all"
                      >
                        <Search className="w-5 h-5" />
                        Analyze Another
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* CTA for non-authenticated users */}
          {!user && (
            <div className="text-center">
              <div className="bg-gradient-to-r from-blue-900/50 to-purple-900/50 border border-blue-700 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-white mb-4">
                  Ready for Unlimited AI Analysis?
                </h3>
                <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
                  Sign up for unlimited website analysis, detailed insights, competitive intelligence, 
                  and access to our community feed where you can like, review, and discover top-performing websites.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link 
                    to="/register" 
                    className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-700 transition-all"
                  >
                    <Sparkles className="w-5 h-5 inline mr-2" />
                    Get Started Free
                  </Link>
                  <Link 
                    to="/pricing" 
                    className="border border-slate-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-slate-800/50 transition-all"
                  >
                    View Pricing
                  </Link>
                </div>
                <p className="text-slate-500 text-sm mt-4">
                  ✅ 10 free analyses • ✅ Full AI insights • ✅ Community access
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}